// src/components/filter/SingleUnitFilter.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IconChevronDown } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import './SingleUnitFilter.css';

/**
 * Build a tree from a flat unit list using parent_unit relationships.
 */
const buildUnitTree = (units) => {
  const tree = [];
  const map = {};

  // Create nodes keyed by unit_name (parent_unit points to unit_name)
  units.forEach((unit) => {
    map[unit.unit_name] = { ...unit, children: [] };
  });

  // Attach children; if parent missing, treat as root
  units.forEach((unit) => {
    if (unit.parent_unit && map[unit.parent_unit]) {
      map[unit.parent_unit].children.push(map[unit.unit_name]);
    } else {
      tree.push(map[unit.unit_name]);
    }
  });

  return tree;
};

/**
 * Flatten the unit tree into options with depth information for indentation.
 * @param {Array} nodes - Tree nodes to flatten
 * @param {number} depth - Current depth level
 * @param {string} visibilityMode - 'frontend' (visibility 1 or 2), 'dashboard' (visibility 0 or 2), or 'all'
 */
const flattenTreeToOptions = (nodes, depth = 0, visibilityMode = 'frontend') => {
  const options = [];
  nodes.forEach((node) => {
    let isDisabled = false;
    
    if (visibilityMode === 'all') {
      // All units are enabled
      isDisabled = false;
    } else if (visibilityMode === 'dashboard') {
      // Dashboard: visibility_level 0 (Dashboard) or 2 (Both) are enabled
      isDisabled = node.visibility_level !== 0 && node.visibility_level !== 2;
    } else {
      // Frontend/Checkpoints: visibility_level 1 (Frontend) or 2 (Both) are enabled
      isDisabled = node.visibility_level !== 1 && node.visibility_level !== 2;
    }
    
    options.push({
      value: node.id,
      label: node.unit_name,
      isDisabled,
      depth,
    });
    if (node.children?.length) {
      options.push(...flattenTreeToOptions(node.children, depth + 1, visibilityMode));
    }
  });
  return options;
};

const SingleUnitFilter = ({ units, selectedUnitId, onSelectUnit, visibilityMode = 'frontend' }) => {
  const [selectedUnitName, setSelectedUnitName] = useState('');

  // Build options from units hierarchy
  const options = useMemo(() => {
    console.log('[SingleUnitFilter DEBUG] units prop received:', units);
    console.log('[SingleUnitFilter DEBUG] units.length:', units?.length);
    console.log('[SingleUnitFilter DEBUG] visibilityMode:', visibilityMode);
    const tree = buildUnitTree(units);
    console.log('[SingleUnitFilter DEBUG] tree after buildUnitTree:', tree);
    const flatOptions = flattenTreeToOptions(tree, 0, visibilityMode);
    console.log('[SingleUnitFilter DEBUG] flatOptions:', flatOptions);
    return flatOptions;
  }, [units, visibilityMode]);

  // Update selected unit name when selectedUnitId changes
  useEffect(() => {
    if (selectedUnitId) {
      const selectedUnit = units.find((unit) => unit.id === selectedUnitId);
      setSelectedUnitName(selectedUnit ? selectedUnit.unit_name : '');
    } else {
      setSelectedUnitName('');
    }
  }, [selectedUnitId, units]);

  // Auto-select the first enabled unit if none is selected
  useEffect(() => {
    if (!selectedUnitId && options.length > 0) {
      const firstEnabledOption = options.find(opt => !opt.isDisabled);
      if (firstEnabledOption) {
        onSelectUnit(firstEnabledOption.value);
      }
    }
  }, [options, selectedUnitId, onSelectUnit]);

  const handleUnitSelect = useCallback((unitId, unitName) => {
    setSelectedUnitName(unitName);
    onSelectUnit(unitId);
  }, [onSelectUnit]);

  const isSingleUnit = units.length === 1;

  return (
    <Dropdown className="single-unit-filter-container">
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="unit-filter-tooltip">
            {isSingleUnit ? 'Du hast nur Zugriff auf eine einzige Einheit.' : ''}
          </Tooltip>
        }
        trigger={isSingleUnit ? ['hover', 'focus'] : []}
      >
        <span>
          <Dropdown.Toggle
            id="dropdown-basic"
            disabled={isSingleUnit}
            style={isSingleUnit ? { backgroundColor: '#e9ecef' } : {}}
          >
            {selectedUnitName || 'Einheit auswählen'}
            {!isSingleUnit && (
              <span className="single-unit-filter-chevron">
                <IconChevronDown size={20} stroke={2} />
              </span>
            )}
          </Dropdown.Toggle>
        </span>
      </OverlayTrigger>
      <Dropdown.Menu>
        {options.map((option) => {
          const isSelected = selectedUnitId === option.value;
          return (
            <div
              key={option.value}
              className={`single-unit-filter-option ${isSelected ? 'is-selected' : ''} ${option.isDisabled ? 'is-disabled' : ''}`}
              onClick={() => !option.isDisabled && handleUnitSelect(option.value, option.label)}
              role="option"
              aria-selected={isSelected}
              aria-disabled={option.isDisabled}
              style={{ paddingLeft: `${20 + (option.depth || 0) * 24}px` }}
            >
              <div className="single-unit-filter-radio">
                <div className="single-unit-filter-radio-inner" />
              </div>
              <span className="single-unit-filter-option-label">{option.label}</span>
            </div>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

SingleUnitFilter.propTypes = {
  units: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      unit_name: PropTypes.string.isRequired,
      level: PropTypes.number,
      parent_unit: PropTypes.string,
      visibility_level: PropTypes.number,
    })
  ).isRequired,
  selectedUnitId: PropTypes.string,
  onSelectUnit: PropTypes.func.isRequired,
  // 'frontend' = visibility 1 or 2, 'dashboard' = visibility 0 or 2, 'all' = all enabled
  visibilityMode: PropTypes.oneOf(['frontend', 'dashboard', 'all']),
};

export default SingleUnitFilter;
